<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { storeToRefs } from "pinia";

import { CategoryFull, CategorySet } from "~/js/interfaces/facets";
import { useTeambuildingState } from "~/js/stores/teambuildings-tm";
import { getGlobalTranslation } from "~/js/composables/translations";

import AlertNoResults from "~/vue/_teammasters/molecules/alerts/AlertNoResults.vue";
import ButtonPrimary from "~/vue/_teammasters/atoms/buttons/ButtonPrimary.vue";
import ButtonToggle from "~/vue/_teammasters/atoms/buttons/ButtonToggle.vue";
import CardTeambuilding from "~/vue/_teammasters/molecules/cards/CardTeambuilding.vue";
import CardTeambuildingSkeleton from "~/vue/_teammasters/molecules/cards/CardTeambuildingSkeleton.vue";
import FilterTeambuildings from "~/vue/_teammasters/molecules/filters/FilterTeambuildings.vue";
import FormTeambuildingFilter from "~/vue/_teammasters/organisms/forms/FormTeambuildingFilter.vue";
import TextH4 from "~/vue/_teammasters/atoms/texts/TextH4.vue";
import TextProse from "~/vue/_teammasters/atoms/texts/TextProse.vue";

/**
 * INTERFACES
 * **/
interface Copy {
    type: string;
    value: string;
}

interface FilterSets {
    categories: Array<CategoryFull | null>;
    durations: Array<CategoryFull | null>;
    locationTypes: Array<CategoryFull | null>;
    participants: Array<CategoryFull | null>;
    priceRanges: Array<CategoryFull | null>;
    purposes: Array<CategoryFull | null>;
    seasonals: Array<CategoryFull | null>;
}

interface PresetFilters {
    categories: Array<number> | null;
    durations: Array<number> | null;
    locationTypes: Array<number> | null;
    new: boolean | null;
    online: boolean | null;
    participants: Array<number> | null;
    priceRanges: Array<number> | null;
    purposes: Array<number> | null;
    seasonals: Array<number> | null;
    spotilight: boolean | null;
}

export interface Props {
    filterSets: FilterSets | null;
    labels: Array<Copy> | Array<null> | any;
    presetFilters: PresetFilters;
    showFilters?: boolean | null;
}
/**
 * VARIABLES
 * **/
// props
const props = defineProps<Props>();

// store
const teambuildingStore = useTeambuildingState();
const { teambuildings, filters, loading, settings, pagination } =
    storeToRefs(teambuildingStore);

// variables
const facetSet: Array<CategorySet> = [
    {
        type: "categories",
        categories: props?.filterSets?.categories,
        isFacet: false,
    },
    {
        type: "purposes",
        categories: props?.filterSets?.purposes,
        isFacet: false,
    },
    {
        type: "priceRanges",
        categories: props?.filterSets?.priceRanges,
        isFacet: true,
    },
    {
        type: "participants",
        categories: props?.filterSets?.participants,
        isFacet: true,
    },
    {
        type: "durations",
        categories: props?.filterSets?.durations,
        isFacet: true,
    },
    {
        type: "locationTypes",
        categories: props?.filterSets?.locationTypes,
        isFacet: true,
    },
];
const activeFilter = ref("categories");

/**
 * COMPUTED
 */
const filterViewOptions = computed(() => {
    return [
        {
            view: "categories",
            text: getGlobalTranslation("type"),
        },
        {
            view: "search",
            text: getGlobalTranslation("name"),
        },
    ];
});

/**
 * METHODS
 **/
const onSearch = (scrollTo: false) => {
    if (scrollTo) {
        const boundingClientRect = document
            .getElementById("teleport-teambuilding")
            ?.getBoundingClientRect();

        if (boundingClientRect) {
            window.scrollTo({
                top: boundingClientRect.top + window.scrollY - 110, // Adjusting scroll position
                behavior: "smooth",
            });
        }
    }

    teambuildingStore.fetch(true);
};

const onClear = (fetch = true) => {
    teambuildingStore.resetFilters(fetch);
};

const onToggleFilter = (view: string) => {
    onClear();
    activeFilter.value = view;
};

const onLoadMore = () => {
    teambuildingStore.fetch(false);
};

/**
 * LIFECYCLES
 **/
onMounted(async () => {
    let fetchByQueryParameters = true;

    // set the categories / purposes filters visible
    if (props.showFilters != null) {
        settings.value.showFilters = props.showFilters;
    }

    // preset the filters based on the CMS settings -> if so -> ignore the query parameters
    if (props.presetFilters && Object.values(props.presetFilters).some(value => value !== null && (Array.isArray(value) ? value.length > 0 : value))) {
        onClear(false);

        Object.entries(props.presetFilters).forEach(([key, value]) => {
            const filter = filters.value?.[key];
            if (filter) {
                if (Object.prototype.hasOwnProperty.call(filter, "values")) {
                    filter.values = value;
                }

                if (Object.prototype.hasOwnProperty.call(filter, "value")) {
                    filter.value = value;
                }
            }
        });

        fetchByQueryParameters = false;
    }

    // fetch the teambuildings
    teambuildingStore.fetch(true, fetchByQueryParameters);
});
</script>

<template>
    <Teleport
        to="#teleport-teambuilding-main-filters"
        v-if="settings.showFilters"
    >
        <legend class="sr-only">
            {{ getGlobalTranslation("Set global filtering for teambuildings") }}
        </legend>

        <div
            class="flex flex-col md:flex-row md:items-center gap-6 justify-between -mt-27 mb-4"
        >
            <h2>
                <TextH4>
                    {{ getGlobalTranslation("Find your teambuilding") }}
                </TextH4>
            </h2>

            <div class="flex flex-col md:flex-row gap-2 md:items-center">
                <TextProse>
                    {{ getGlobalTranslation("Search by") }}:
                </TextProse>
                <ButtonToggle
                    :options="filterViewOptions"
                    :on="activeFilter"
                    @active-filter="onToggleFilter($event)"
                />
            </div>
        </div>

        <FormTeambuildingFilter
            :active-filter="activeFilter"
            :categories="filterSets?.categories ?? null"
            :default-categories="filters.categories.values"
            :default-purposes="filters.purposes.values"
            :purposes="filterSets?.purposes ?? null"
            @search="onSearch($event)"
            utilities="mb-4"
            v-if="filters"
        />
    </Teleport>
    <Teleport to="#teleport-teambuilding-facet-filters">
        <FilterTeambuildings
            :filterset="facetSet"
            :labels="labels"
            :show-filters="settings.showFilters"
            @search="onSearch(false)"
        />
    </Teleport>
    <Teleport to="#teleport-teambuilding">
        <!-- loading skeleton -->
        <CardTeambuildingSkeleton v-if="loading" />
        <CardTeambuildingSkeleton v-if="loading" />
        <CardTeambuildingSkeleton v-if="loading" />
        <CardTeambuildingSkeleton v-if="loading" />
        <CardTeambuildingSkeleton v-if="loading" />
        <CardTeambuildingSkeleton v-if="loading" />

        <!-- no results -->
        <AlertNoResults
            v-if="teambuildings && teambuildings.length == 0 && !loading"
            :heading="getGlobalTranslation('no_teambuilding_results_heading')"
            :message="
                getGlobalTranslation('no_teambuilding_results_description')
            "
            :button="getGlobalTranslation('no_teambuilding_results_button')"
            utilities="md:col-span-2 lg:col-span-3"
            @action="onClear"
        />

        <!-- teambuilding cards -->
        <template v-if="teambuildings && !loading">
            <CardTeambuilding
                v-for="teambuilding in teambuildings.map((tb) => tb.document)"
                :key="teambuilding.id"
                :card="teambuilding"
            />
        </template>

        <!-- load more -->
        <div
            v-if="
                !loading &&
                    pagination.current < pagination.totalPages &&
                    pagination.current != pagination.totalPages
            "
            class="md:col-span-2 lg:col-span-3"
        >
            <ButtonPrimary
                utilities="mx-auto !block mt-4"
                type="button"
                :content="getGlobalTranslation('Load more')"
                @click="onLoadMore"
            />
        </div>
    </Teleport>
</template>
